import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useIntegrations, useTestIntegration } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import type { IntegrationProvider } from '@/types/models';

export default function IntegrationsPage() {
  const { data: integrations = [] } = useIntegrations();
  const testIntegration = useTestIntegration();

  const handleTest = (provider: IntegrationProvider) => {
    testIntegration.mutate(provider, {
      onSuccess: (res) => toast.success(res.success ? `${provider} connection verified` : `${provider} test failed`),
      onError: () => toast.error(`Failed to test ${provider}`),
    });
  };

  return (
    <>
      <PageHeader title="Integrations" description="Manage third-party service connections" />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {integrations.map((integration) => (
          <Card key={integration.provider}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{integration.display_name}</CardTitle>
                {integration.is_configured ? (
                  <Badge variant="default" className="gap-1 bg-status-complete/10 text-status-complete">
                    <CheckCircle className="h-3 w-3" /> Connected
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 text-muted-foreground">
                    <XCircle className="h-3 w-3" /> Not configured
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{integration.description}</p>
              {integration.last_verified_at && (
                <p className="text-xs text-muted-foreground">Last verified: {new Date(integration.last_verified_at).toLocaleDateString()}</p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={!integration.is_configured || testIntegration.isPending} onClick={() => handleTest(integration.provider)}>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Test
                </Button>
                <Button variant="outline" size="sm">Configure</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
